/**
 * Claiming a trust, and deciding who gets in.
 *
 * Wraps the four endpoints behind Beno's 2026-08-18 ask — "somebody, and maybe
 * the principal that created a trust, must actually then verify that it is this
 * person before he releases access into the database":
 *
 *   POST /v1/trusts/claim                          — I am a trustee of IT …/…
 *   GET  /v1/trust-access-requests                 — who is asking to get in
 *   POST /v1/trust-access-requests/{id}/approve    — let them in, at a tier
 *   POST /v1/trust-access-requests/{id}/reject     — do not
 *
 * ── The claim response tells you NOTHING, on purpose ─────────────────────────
 * SA trust numbers are formatted and guessable (`IT 610/2025(C)`), so an
 * endpoint that confirmed existence would be an enumeration oracle over the
 * national register. A hit and a miss return the SAME 202 with the SAME body.
 *
 * That constrains this UI more than it first looks. After a successful claim we
 * may say only what the server said — "if that trust is on Elreign, the person
 * who registered it has been asked" — and never "request sent" or "found it".
 * A success screen that implies the trust exists hands back exactly the signal
 * the backend spent a constant-time pad withholding. Do not "improve" the
 * wording into a confirmation.
 *
 * ── The approver is shown one checked fact and two claimed ones ──────────────
 * `requesterEmail` is verified (they control the address). `requesterStatedName`
 * and `claimedTier` are typed by the person asking and are worth exactly what
 * that implies. `deedCheck` is the only independent evidence, and its fourth
 * verdict is the important one: `no-names-on-file` means the deed was never
 * ingested, so "no one by this name appears in your deed" would be a confident
 * falsehood. Render the four verdicts as four different things.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

/** What a claimant says they are. A hint for the approver, never a grant — the
 *  tier that lands is the one the APPROVER picks. */
export type ClaimedTier = 'trustee' | 'beneficiary';

/** Why an approver said no. Recorded for the firm; never shown to the claimant.
 *  `not-this-trust` is the one with a side effect — it is the signal that two
 *  tenants hold the same IT number and a real person was misrouted. */
export type RejectReason =
  | 'not-this-person'
  | 'not-this-trust'
  | 'no-longer-involved'
  | 'other';

/** Does the claimant's stated name appear in the trust's own deed?
 *
 *  `no-names-on-file` is NOT a no-match. It means we have no roster and no BO
 *  register for this trust, so we cannot say either way — the approver has to
 *  fall back on knowing the person. Collapsing it into `no-match` would tell a
 *  principal that a legitimate trustee is a stranger. */
export type DeedCheckVerdict = 'match' | 'partial-match' | 'no-match' | 'no-names-on-file';

export interface DeedCheck {
  verdict: DeedCheckVerdict;
  /** Names from the deed that the stated name matched, if any. */
  matched: string[];
  /** A sentence the server composed, so the screen and the audit row cannot
   *  describe the same verdict differently. Prefer it over rewriting it here. */
  statement: string;
}

/** The trust's founder, for the "verify with founder" panel on a request
 *  (Paul's ruling, 15 Sep 2026).
 *
 *  `source` is `'deed'` and only ever `'deed'`: the backend builds this block
 *  from the trust's own beneficial-ownership register row for the person who
 *  founded it, which ingestion mints off the deed pack. It is NOT built from
 *  the trustee roster's role label, because the app writes that label itself.
 *  If a second provenance ever appears it will be a new member of the union, so
 *  a screen that switches on `source` keeps telling the truth.
 *
 *  `phone` and `email` are reg 3C(1) contact particulars. They arrive only for
 *  the privileged read tier and only when the register actually holds them, so
 *  render whichever of them is present and show no channel at all when neither
 *  is. Do not substitute the requester's own email: that is the person asking
 *  to get in, not the person being rung to vouch for them. */
export interface FounderContact {
  name: string;
  phone?: string;
  email?: string;
  source: 'deed';
}

export interface TrustAccessRequestView {
  id: string;
  trustId: string;
  trustName: string;
  registrationKey: string;
  /** Verified — they control this address. The only checked fact on the row. */
  requesterEmail: string;
  /** Typed by the claimant. Not verified by anything. */
  requesterStatedName: string;
  claimedTier: ClaimedTier;
  decisionRoute: 'principal' | 'tenant-admins';
  /** True when the trust had no registered principal, so the firm is being
   *  asked as the HOLDER of the trust rather than as its owner. 150 of dev's
   *  212 registry rows are in this state and it is the normal case for a
   *  firm-stewarded client, not an edge — say so on the row. */
  askedAsHolder: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  expiresAt: string;
  deedCheck: DeedCheck;
  /** Absent whenever the trust's register does not name exactly one founder.
   *  Absent means SHOW NOTHING - no placeholder name, no "unknown", no empty
   *  card. The officer falls back on the deed check and on knowing the person. */
  founder?: FounderContact;
  /** The deed-signing date, ISO `YYYY-MM-DD`, off the trust's own record.
   *  Absent when no document gave one, which is the normal state of a
   *  governance-only or name-only pack.
   *
   *  THE CORROBORATION LINE. "Founder, per the deed signed <deedDate>" may be
   *  written ONLY when `founder` AND `deedDate` are both present. With a
   *  founder and no date, name the founder and say nothing about a deed. The
   *  "2020" in the redesign mock is a placeholder: never hard-code a year. */
  deedDate?: string;
}

export interface TrustAccessRequestsResponse {
  requests: TrustAccessRequestView[];
  correlationId: string;
}

export interface ClaimTrustInput {
  /** AS TYPED. The server folds it — never pre-fold here, and never send a
   *  registry key, which would let the caller probe the index's id space. */
  registration: string;
  fullName: string;
  claimedTier?: ClaimedTier;
}

export interface ClaimTrustResponse {
  accepted: true;
  /** Identical for a hit and a miss. Show THIS, never a message of our own. */
  message: string;
  correlationId: string;
}

export const TRUST_ACCESS_REQUESTS_QUERY_KEY = ['trust-access-requests'] as const;

/** The approver's queue. Tenant admins see the whole tenant's; a named
 *  principal sees only their own rows. Someone with neither claim gets an empty
 *  list rather than a 403, so this is safe to mount for any admin chair. */
export function useTrustAccessRequests(opts: { enabled?: boolean } = {}) {
  return useQuery<TrustAccessRequestsResponse>({
    queryKey: TRUST_ACCESS_REQUESTS_QUERY_KEY,
    queryFn: ({ signal }) =>
      api<TrustAccessRequestsResponse>('/api/v1/trust-access-requests', { signal }),
    enabled: opts.enabled ?? true,
  });
}

/** Ask for access to a trust by its registration number.
 *
 *  Deliberately does NOT invalidate the requests queue: the claimant is not the
 *  approver, and a claim tells the claimant's own session nothing new. */
export function useClaimTrust() {
  return useMutation<ClaimTrustResponse, ApiError, ClaimTrustInput>({
    mutationFn: (input) =>
      api<ClaimTrustResponse>('/api/v1/trusts/claim', {
        method: 'POST',
        // `api()` serialises `body` itself — pre-stringifying here double-encodes
        // and the backend rejects "Expected object, received string" (found live
        // on dev, 2026-08-20, on all three mutations in this file).
        body: input,
      }),
  });
}

export interface ApproveInput {
  id: string;
  /** The tier the APPROVER grants. Omit to accept what was claimed. */
  tier?: ClaimedTier;
  notes?: string;
}

export interface ApproveResponse {
  approved: true;
  /** False when the grant landed on the trust but the workspace link did not.
   *  The person can be a member of the trust and still have no way to reach it,
   *  because every trust read resolves off the caller's own tenant claim — the
   *  2026-08-07 memberships failure. Surface it; do not report a clean yes. */
  workspaceAccessReady: boolean;
  correlationId: string;
}

export function useApproveTrustAccess() {
  const qc = useQueryClient();
  return useMutation<ApproveResponse, ApiError, ApproveInput>({
    mutationFn: ({ id, tier, notes }) =>
      api<ApproveResponse>(
        `/api/v1/trust-access-requests/${encodeURIComponent(id)}/approve`,
        { method: 'POST', body: { ...(tier ? { tier } : {}), ...(notes ? { notes } : {}) } },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TRUST_ACCESS_REQUESTS_QUERY_KEY }); },
  });
}

export interface RejectInput {
  id: string;
  reason?: RejectReason;
  notes?: string;
}

export function useRejectTrustAccess() {
  const qc = useQueryClient();
  return useMutation<{ rejected: true; correlationId: string }, ApiError, RejectInput>({
    mutationFn: ({ id, reason, notes }) =>
      api<{ rejected: true; correlationId: string }>(
        `/api/v1/trust-access-requests/${encodeURIComponent(id)}/reject`,
        { method: 'POST', body: { ...(reason ? { reason } : {}), ...(notes ? { notes } : {}) } },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TRUST_ACCESS_REQUESTS_QUERY_KEY }); },
  });
}

/** How each verdict should READ to an approver. Kept beside the type so a new
 *  verdict cannot be added without someone deciding what it means on screen. */
export const DEED_CHECK_TONE: Readonly<Record<DeedCheckVerdict, 'good' | 'caution' | 'bad' | 'neutral'>> = {
  'match': 'good',
  'partial-match': 'caution',
  'no-match': 'bad',
  'no-names-on-file': 'neutral',
};

export const REJECT_REASON_LABEL: Readonly<Record<RejectReason, string>> = {
  'not-this-person': 'I do not recognise this person',
  'not-this-trust': 'This is not my trust. The number belongs to someone else',
  'no-longer-involved': 'They were involved, but no longer are',
  'other': 'Another reason',
};
